import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Character } from '../types/Character';
import { useTownState } from '../context/TownStateContext';
import './CharacterComponent.css';

interface CharacterComponentProps {
  character: Character;
  isSelected: boolean;
  onSelect: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

const CharacterComponent: React.FC<CharacterComponentProps> = ({ character, isSelected, onSelect }) => {
  const { characters } = useTownState();
  const [containerSize, setContainerSize] = useState({ width: 800, height: 800 });
  const prevPositionRef = useRef(character.position);

  // CRITICAL: Get EXACT container size for precise calculations
  useEffect(() => {
    const updateSize = () => {
      const container = document.querySelector('.town-container') as HTMLElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        setContainerSize({ width: rect.width, height: rect.height });
      }
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    const container = document.querySelector('.town-container');
    if (container) observer.observe(container);

    return () => {
        if (container) observer.unobserve(container);
    }
  }, []);

  useEffect(() => {
    prevPositionRef.current = character.position;
  }, [character.position]);

  const distance = Math.hypot(
    character.position.x - prevPositionRef.current.x,
    character.position.y - prevPositionRef.current.y
  );

  const duration = Math.max(0.8, distance * 0.15); // Smooth animation duration

  // PRECISE CALCULATION: Account for CSS Grid gaps and exact cell size
  const GRID_SIZE = 32;
  const GAP_SIZE = 1;
  const TOTAL_GAPS = (GRID_SIZE - 1) * GAP_SIZE;
  
  const cellWidth = (containerSize.width - TOTAL_GAPS) / GRID_SIZE;
  const cellHeight = (containerSize.height - TOTAL_GAPS) / GRID_SIZE;
  
  // Calculate position with gap offset
  const pixelX = character.position.x * (cellWidth + GAP_SIZE) + cellWidth / 2;
  const pixelY = character.position.y * (cellHeight + GAP_SIZE) + cellHeight / 2;
  
  const prevPixelX = prevPositionRef.current.x * (cellWidth + GAP_SIZE) + cellWidth / 2;
  const prevPixelY = prevPositionRef.current.y * (cellHeight + GAP_SIZE) + cellHeight / 2;

  // Determine which emoji to use based on character.sprite or name
  let emoji = '👤'; // Default human
  if (character.name.includes('Ada') || character.personality.includes('teacher')) emoji = '👩‍🏫';
  else if (character.name.includes('Ben') || character.personality.includes('shopkeeper')) emoji = '👨‍💼';
  else if (character.name.includes('Charlie') || character.personality.includes('student')) emoji = '👦';
  else if (character.name.includes('Diana') || character.personality.includes('chef')) emoji = '👩‍🍳';

  return (
    <motion.div
      className={`character ${isSelected ? 'selected' : ''}`}
      animate={{
        left: `${pixelX}px`,
        top: `${pixelY}px`,
      }}
      initial={{
        left: `${prevPixelX}px`,
        top: `${prevPixelY}px`,
      }}
      transition={{ 
        duration, 
        ease: 'easeInOut',
        type: 'tween'
      }}
      onAnimationComplete={() => {
        prevPositionRef.current = character.position;
      }}
      style={{
        width: `${Math.min(cellWidth, cellHeight) * 0.8}px`,
        height: `${Math.min(cellWidth, cellHeight) * 0.8}px`,
        zIndex: 10,
      }}
      onClick={onSelect}
      title={character.name}
    >
      {emoji}
    </motion.div>
  );
};

// Exportamos tanto por defecto como nombrado
export default CharacterComponent;
export { CharacterComponent };